/**
 * Single-scenario detail loader with IndexedDB caching.
 *
 * Fetches per-scenario JSON files from R2 CDN (~3-5KB each),
 * caches in IndexedDB with 48h TTL for instant subsequent loads.
 *
 * Usage:
 *   const detail = await loadScenarioById('uuid-here');
 *   // detail includes full procedures, AI text, nationalPricing
 */

import i18n from '@/i18n';

const CDN_BASE = 'https://static.americanhealthprices.com/data';
// Bumped when the per-scenario JSON shape changes — busts both browser HTTP
// cache and IndexedDB cache so users get the new fields immediately. R2
// doesn't send Cache-Control headers, so the browser otherwise heuristically
// caches for days.
const DATA_VERSION = '2';

const DB_NAME = 'ahp-scenario-detail-v1';
const DB_VERSION = 1;
const STORE_NAME = 'details';
const STALE_MS = 48 * 60 * 60 * 1000; // 48 hours

export interface ScenarioDetailProcedure {
  code: string;
  codeType: string;
  description: string;
  phase: string;
  quantity: number;
  estimatedCost: number;
  dayNumber?: number;
  setting?: string;
}

export interface ScenarioDetail {
  id: string;
  uuid: string;
  slug: string;
  name: string;
  iconName: string;
  color: string;
  description: string;
  duration: string;
  totalProcedures: number;
  estimatedCost: number;
  minCost?: number;
  maxCost?: number;
  phaseBreakdown?: Record<string, number>;
  phases: string[];
  procedures: ScenarioDetailProcedure[];
  createdBy?: string;
  isVerified?: boolean;
  helpfulCount?: number;
  usageCount?: number;
  saveCount?: number;
  tags?: string[];
  severityLevel?: number;
  category?: string;
  source?: string;
  scenarioFamilyId?: string;
  familyVariantLabel?: string;
  aiConsumerMessage?: string;
  aiClinicalSummary?: string;
  aiCostAnalysis?: string;
  aiPreparationGuide?: string;
  nationalPricing?: Record<string, { p: number; h: number; s: string; n?: string; m?: number }>;
}

interface CachedEntry {
  generated_at: string;
  data: ScenarioDetail;
  cached_at: number;
}

// ── IndexedDB helpers ─────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getFromIDB(key: string): Promise<CachedEntry | null> {
  try {
    const db = await openDB();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch {
    return null;
  }
}

async function putToIDB(key: string, data: CachedEntry): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(data, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IndexedDB write failure is non-fatal
  }
}

// ── In-memory cache + deduplication ───────────────────────────────────────

const memoryCache = new Map<string, ScenarioDetail>();
const inflightRequests = new Map<string, Promise<ScenarioDetail | null>>();

/**
 * Load a single scenario by UUID from R2 CDN.
 * Returns full scenario data (procedures, AI text, national pricing).
 * Caches in IndexedDB for 48h and in memory for the session.
 */
export async function loadScenarioById(uuid: string): Promise<ScenarioDetail | null> {
  if (!uuid) return null;

  // 1. Memory cache (instant)
  const mem = memoryCache.get(uuid);
  if (mem) return mem;

  // 2. Deduplicate concurrent fetches
  const inflight = inflightRequests.get(uuid);
  if (inflight) return inflight;

  const promise = _fetchScenarioDetail(uuid);
  inflightRequests.set(uuid, promise);

  try {
    return await promise;
  } finally {
    inflightRequests.delete(uuid);
  }
}

async function _fetchScenarioDetail(uuid: string): Promise<ScenarioDetail | null> {
  // 1. Check IndexedDB — but only if it has the current DATA_VERSION
  const idbKey = `scenario-detail:${uuid}:v${DATA_VERSION}`;
  const cached = await getFromIDB(idbKey);
  if (cached && cached.data && (Date.now() - cached.cached_at) < STALE_MS) {
    memoryCache.set(uuid, cached.data);
    return cached.data;
  }

  // 2. Fetch from R2 CDN with cache-busting query string
  try {
    const resp = await fetch(`${CDN_BASE}/scenarios/${uuid}.json?v=${DATA_VERSION}`);
    if (!resp.ok) {
      console.warn(`[ScenarioDetail] Fetch failed for ${uuid}:`, resp.status);
      return null;
    }

    const json = await resp.json();
    const detail: ScenarioDetail = json.data;

    if (!detail || !detail.id) return null;

    // Normalize phases
    if (Array.isArray(detail.phases)) {
      detail.phases = detail.phases.map((p: any) =>
        typeof p === 'string' ? p : (p?.name || String(p))
      );
    }

    // Cache in memory + IDB
    memoryCache.set(uuid, detail);
    putToIDB(idbKey, {
      generated_at: json.generated_at,
      data: detail,
      cached_at: Date.now(),
    });

    return detail;
  } catch (e) {
    console.warn(`[ScenarioDetail] Error fetching ${uuid}:`, e);
    return null;
  }
}

/**
 * Populate nationalPricing ref from a per-scenario detail.
 * Returns the pricing map ready to be assigned to staticNationalPricingRef.
 */
export function extractNationalPricing(
  detail: ScenarioDetail
): Record<string, { p: number; h: number; s: string; n?: string }> | null {
  return detail.nationalPricing && Object.keys(detail.nationalPricing).length > 0
    ? detail.nationalPricing
    : null;
}

// ── International pricing for scenario cards ────────────────────────────

interface IntlScenarioCost {
  scenario_id: string;
  country_code: string;
  mapped_cpt_codes: number;
  total_cpt_codes: number;
  intl_total_cost: number;
  us_national_total_cost: number;
}

interface IntlCountry {
  country_code: string;
  country_name: string;
  flag_emoji: string | null;
}

interface IntlStatsFile {
  scenarioCosts?: IntlScenarioCost[];
  countries?: IntlCountry[];
}

// Cached once per session — international_stats.json (~200KB gzipped)
let intlStatsPromise: Promise<IntlStatsFile | null> | null = null;

async function loadIntlStats(): Promise<IntlStatsFile | null> {
  if (!intlStatsPromise) {
    intlStatsPromise = (async () => {
      try {
        const { loadStaticData } = await import('@/lib/offlineData');
        return await loadStaticData<IntlStatsFile>('international_stats.json');
      } catch {
        return null;
      }
    })();
  }
  return intlStatsPromise;
}

export interface ScenarioIntlPricingEntry {
  name: string;
  flag: string;
  total: number;
  phases: Record<string, number>;
  mapped: number;
  totalCodes: number;
  /** US national cost of the SAME mapped CPT subset (from the
   *  international_scenario_costs MV). Comparisons must use this — not the
   *  full scenario cost — so partial-coverage countries aren't presented as
   *  "400× cheaper" against procedures they don't include. */
  usTotal?: number;
}

/**
 * Load international pricing for a specific scenario.
 * Fetches international_stats.json once (cached), then filters for the scenario.
 * Returns a Record<countryCode, entry> matching ScenarioCard's InternationalPricing type.
 */
export async function loadScenarioIntlPricing(
  scenarioId: string
): Promise<Record<string, ScenarioIntlPricingEntry>> {
  const stats = await loadIntlStats();
  if (!stats?.scenarioCosts?.length || !stats?.countries?.length) return {};

  const countryMap = new Map<string, IntlCountry>();
  for (const c of stats.countries) countryMap.set(c.country_code, c);

  const result: Record<string, ScenarioIntlPricingEntry> = {};
  for (const row of stats.scenarioCosts) {
    if (row.scenario_id !== scenarioId) continue;
    const country = countryMap.get(row.country_code);
    if (!country || !row.intl_total_cost) continue;
    result[row.country_code] = {
      name: country.country_name,
      flag: country.flag_emoji || '',
      total: row.intl_total_cost,
      phases: {},
      mapped: row.mapped_cpt_codes,
      totalCodes: row.total_cpt_codes,
      usTotal: Number(row.us_national_total_cost) > 0 ? Number(row.us_national_total_cost) : undefined,
    };
  }
  return result;
}
